import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import { Location, ApiResponse } from '../types';

export interface LocationFilters {
  page?: number;
  size?: number;
  sort?: string;
  filter?: string;
}

export interface CreateLocationData {
  ycl_name: string;
  ycl_code: string;
  ycl_type?: string;
  ycl_city?: string;
  ycl_province?: string;
  ycl_postal_code?: string;
  ycl_country?: string;
  ycl_latitude?: number;
  ycl_longitude?: number;
  ycl_description?: string;
  ycl_address?: string;
  ycl_caller_id?: string;
  ycl_company?: number;
  ycl_events_enabled?: boolean;
  ycl_entry_logging?: boolean;
  ycl_defaut_location?: boolean;
  location_timezone?: string;
  status?: boolean;
  
  // NVR Configuration
  nvr_ip?: string | null;
  nvr_port?: number;
  nvr_internal_port?: number;
  nvr_rtsp_port?: number;
  nvr_internal_rtsp_port?: number;
  nvr_username?: string | null;
  nvr_password_encrypted?: string;
  nvr_type?: string;
  nvr_model?: string;
  nvr_channel_count?: number;
  nvr_connection_status?: string;
  nvr_last_connected?: string;
  
  // Transcoding Device
  transcoding_enabled?: boolean;
  transcoding_device_ip?: string;
  transcoding_device_port?: number;
  transcoding_device_api_port?: number;
  transcoding_device_username?: string;
  transcoding_device_password_encrypted?: string;
  
  // External Access
  external_hostname?: string;
  external_http_port?: number | null;
  external_transcoding_port?: number;
  external_rtsp_port?: number;
  external_access_enabled?: boolean;
  
  // Stream Authentication
  stream_auth_enabled?: boolean;
  stream_auth_port?: number;
  location_identifier?: string;
  stream_shared_secret?: string;
  
  // Cloud Integration Credentials
  api_key?: string | null;
  app_secret?: string | null;
}

export interface UpdateLocationData extends Partial<CreateLocationData> {}

class LocationService {
  /**
   * Get all locations with optional filters
   */
  async getLocations(filters?: LocationFilters): Promise<ApiResponse<Location[]>> {
    return apiClient.get<ApiResponse<Location[]>>(
      API_CONFIG.ENDPOINTS.LOCATIONS,
      filters
    );
  }

  /**
   * Get location by ID
   */
  async getLocationById(id: number): Promise<Location> {
    return apiClient.get<Location>(`${API_CONFIG.ENDPOINTS.LOCATION}/${id}`);
  }

  /**
   * Create new location
   */
  async createLocation(data: CreateLocationData): Promise<any> {
    const location = await apiClient.post<Location>(API_CONFIG.ENDPOINTS.LOCATION, data);
    
    // Auto-sync cameras if cloud provider is specified
    const cloudProvider = (data as any).cloud_provider;
    if (cloudProvider && (data as any).cloud_sync_enabled) {
      try {
        const syncResult = await this.syncCloudCameras(location.ycl_id);
        return {
          ...location,
          cloud_sync: {
            success: true,
            cameras_synced: syncResult.sync_result?.cameras_synced || 0,
            cameras_failed: syncResult.sync_result?.errors?.length || 0,
            message: syncResult.detail
          }
        };
      } catch (syncError: any) {
        console.error('Cloud sync failed:', syncError);
        return {
          ...location,
          cloud_sync: {
            success: false,
            cameras_synced: 0,
            cameras_failed: 0,
            message: syncError.message || 'Cloud sync failed'
          }
        };
      }
    }
    
    return location;
  }

  /**
   * Update location
   */
  async updateLocation(id: number, data: UpdateLocationData): Promise<Location> {
    return apiClient.put<Location>(`${API_CONFIG.ENDPOINTS.LOCATION}/${id}`, data);
  }

  /**
   * Delete location
   * @param id Location ID
   * @param force If true, cascade delete all related cameras and events
   */
  async deleteLocation(id: number, force: boolean = false): Promise<{ success: boolean; message: string; deleted?: any }> {
    const url = force 
      ? `${API_CONFIG.ENDPOINTS.LOCATION}/${id}?force=true`
      : `${API_CONFIG.ENDPOINTS.LOCATION}/${id}`;
    return apiClient.delete<{ success: boolean; message: string; deleted?: any }>(url);
  }

  /**
   * Search locations
   */
  async searchLocations(searchTerm: string, page = 1, size = 20): Promise<ApiResponse<Location[]>> {
    return this.getLocations({
      filter: searchTerm,
      page,
      size,
    });
  }

  /**
   * Get locations by city
   */
  async getLocationsByCity(city: string): Promise<ApiResponse<Location[]>> {
    return this.getLocations({
      filter: `location_city::${city}`,
    });
  }

  /**
   * Get locations by state
   */
  async getLocationsByState(state: string): Promise<ApiResponse<Location[]>> {
    return this.getLocations({
      filter: `location_state::${state}`,
    });
  }

  /**
   * Fetch and store playbacks from hardware
   */
  async fetchStorePlaybacks(locationId: number): Promise<{ message: string; count: number }> {
    return apiClient.post<{ message: string; count: number }>(
      '/camera-security/fetch-store-playbacks',
      { location_id: locationId }
    );
  }

  /**
   * Sync cameras from cloud provider (HikVision/Dahua/Ubiquiti)
   */
  async syncCloudCameras(locationId: number): Promise<{ detail: string; sync_result: any }> {
    return apiClient.post<{ detail: string; sync_result: any }>(
      `${API_CONFIG.ENDPOINTS.LOCATION}/${locationId}/sync-cloud-cameras`,
      {}
    );
  }
}

export const locationService = new LocationService();
